/**
 * API Client for Cohort Manager
 *
 * Uses AWS Amplify REST API with automatic SigV4 signing via Cognito Identity Pool.
 * User identity is extracted server-side from the Cognito IAM auth context.
 */

import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthStore } from '../stores/authStore';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: string;
  message: string;
  statusCode?: number;
}

export class ApiError extends Error {
  public readonly response: ApiErrorResponse;
  constructor(resp: ApiErrorResponse) {
    super(resp.message || resp.error || 'API request failed');
    this.name = 'ApiError';
    this.response = resp;
  }
}

/**
 * Token expiry buffer — refresh the token if it expires within this many seconds.
 * Cognito ID tokens are typically valid for 1 hour; we refresh 2 minutes early.
 */
const TOKEN_EXPIRY_BUFFER_SECONDS = 120;

/**
 * Load the test user config from the local config file (gitignored).
 * Returns null in production or when no test user is configured.
 * The dynamic import is cached by Vite after the first call.
 */
async function getTestUser(): Promise<{ alias: string; name: string } | null> {
  if (import.meta.env.PROD) return null;
  try {
    const mod = await import('../config/test-user.config');
    if (!mod.testUser?.alias) return null;
    return { alias: mod.testUser.alias, name: mod.testUser.name };
  } catch {
    return null;
  }
}

/**
 * Get a fresh, valid ID token for the X-Id-Token header.
 * Always checks expiry and proactively refreshes before the token expires.
 * If the session is truly gone, redirects to the SSO login page.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  let session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;

  // Check if the token is missing or about to expire
  const expiresAt = idToken?.payload?.exp as number | undefined;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isExpiredOrExpiring = !idToken || expiresAt === undefined || (expiresAt - nowSeconds < TOKEN_EXPIRY_BUFFER_SECONDS);

  if (isExpiredOrExpiring) {
    // Force a token refresh
    try {
      session = await fetchAuthSession({ forceRefresh: true });
    } catch {
      // Refresh failed — session is gone, show the session expired screen
      markSessionExpired();
      await new Promise(() => {}); // halt execution
    }
  }

  const freshToken = session.tokens?.idToken?.toString();
  if (!freshToken) {
    markSessionExpired();
    await new Promise(() => {}); // halt execution
  }

  const headers: Record<string, string> = { 'X-Id-Token': freshToken! };

  // In dev, inject test user headers if a local config is present.
  // The backend verifies the real JWT and checks the admin POSIX group
  // before honoring this override.
  const testUser = await getTestUser();
  if (testUser) {
    headers['X-Test-User'] = testUser.alias;
    headers['X-Test-User-Name'] = testUser.name;
  }

  return headers;
}

/**
 * Signal that the session has expired.
 * Sets the isSessionExpired flag in the auth store, which causes the app
 * to show the "session expired" screen. The user then clicks "Sign in"
 * to initiate the OIDC flow from a fresh user interaction — avoiding the
 * invalid_state error that occurs when signInWithRedirect is called from
 * a background context (e.g. a polling timer).
 */
function markSessionExpired(): void {
  useAuthStore.setState({ isSessionExpired: true, isAuthenticated: false });
}

/**
 * Check an API response for auth errors and show session expired screen if needed.
 * API Gateway returns 403 for IAM auth failures; our Lambda returns 401 for expired JWT.
 * Note: 403 from our Lambda means "forbidden" (not authorized), not an expired session —
 * only 401 should trigger the session expired screen.
 */
async function checkForAuthError(response: any): Promise<void> {
  const statusCode = response?.statusCode ?? response?.response?.statusCode;
  if (statusCode === 401) {
    markSessionExpired();
    await new Promise(() => {}); // halt execution
  }
}

/**
 * Handle Amplify HTTP errors — Amplify throws on non-2xx responses.
 * Only treat 401 as a session expiry; 403 is a permission error and should
 * surface as a normal API error so the UI can display it to the user.
 */
async function handleAmplifyError(err: any): Promise<void> {
  const statusCode = err?.response?.statusCode ?? err?.statusCode;
  if (statusCode === 401) {
    markSessionExpired();
    await new Promise(() => {}); // halt execution
  }
}

/**
 * API Client with typed methods using Amplify REST.
 * SigV4 signing handles API Gateway IAM auth automatically.
 * X-Id-Token header provides verified user identity to the backend.
 */
export const apiClient = {
  async get<T>(path: string): Promise<T> {
    const headers = await getAuthHeaders();
    try {
      const restOperation = get({ apiName: 'cohortApi', path, options: { headers } });
      const { body } = await restOperation.response;
      const result = await body.json() as unknown as T;
      await checkForAuthError(result);
      return result;
    } catch (err: any) {
      await handleAmplifyError(err);
      throw err;
    }
  },

  async post<T>(path: string, reqBody?: unknown): Promise<T> {
    const headers = await getAuthHeaders();
    try {
      const restOperation = post({ apiName: 'cohortApi', path, options: { headers, body: reqBody as any } });
      const { body } = await restOperation.response;
      const result = await body.json() as unknown as T;
      await checkForAuthError(result);
      return result;
    } catch (err: any) {
      await handleAmplifyError(err);
      throw err;
    }
  },

  async put<T>(path: string, reqBody?: unknown): Promise<T> {
    const headers = await getAuthHeaders();
    try {
      const restOperation = put({ apiName: 'cohortApi', path, options: { headers, body: reqBody as any } });
      const { body } = await restOperation.response;
      const result = await body.json() as unknown as T;
      await checkForAuthError(result);
      return result;
    } catch (err: any) {
      await handleAmplifyError(err);
      throw err;
    }
  },

  async delete<T>(path: string): Promise<T> {
    const headers = await getAuthHeaders();
    try {
      const restOperation = del({ apiName: 'cohortApi', path, options: { headers } });
      const { body } = await restOperation.response;
      let result: T;
      try {
        result = await body.json() as unknown as T;
      } catch {
        // Empty response body — treat as success
        return { success: true } as unknown as T;
      }
      await checkForAuthError(result);
      return result;
    } catch (err: any) {
      await handleAmplifyError(err);
      throw err;
    }
  },
};
